import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LaunchIcon from '@mui/icons-material/Launch';
import ReplayIcon from '@mui/icons-material/Replay';
import SettingsIcon from '@mui/icons-material/Settings';
import { Button, Stack, Typography } from '@mui/material';
import { useGetInstances, useGetRenkiTag } from 'gen/client';

import { useGetTagRedirectLink } from '@ui/Utils/scan/tag/useGetTagRedirectLink';

interface TagOwnerViewProps {
    onOpenLink: () => void;
    onScanAgain: () => void;
    serial: string;
}

export default function TagOwnerView({ onOpenLink, onScanAgain, serial }: Readonly<TagOwnerViewProps>) {
    const { data: instances = [] } = useGetInstances({
        swr: {
            refreshInterval: 0,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
        },
    });

    const { data: tagData } = useGetRenkiTag(serial, {
        swr: {
            enabled: !!serial,
            refreshInterval: 0,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
        },
    });

    const { tagRedirectLink } = useGetTagRedirectLink(serial);
    const hasDefaultLink = tagRedirectLink?.link === 'https://renki.app';

    const tagInstance = instances.find((inst) => inst.cloak_role === tagData?.tag?.instance);
    const instanceDomain = tagInstance ? tagInstance.domain : '';

    const navigateToSettings = () => {
        // Navigate to tag settings page
        window.open(`${instanceDomain}/admin/devices/tag/${serial}`);
    };

    return (
        <Stack
            direction="column"
            spacing={2}
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
            }}
        >
            <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 56 }} />
            <Stack spacing={0.5} sx={{ textAlign: 'center' }}>
                <Typography variant="h5">Tag found</Typography>
                <Typography
                    sx={{
                        color: 'text.secondary',
                    }}
                    variant="body2"
                >
                    You are the owner of this tag.
                </Typography>
                <Typography
                    sx={{
                        color: 'text.secondary',
                    }}
                    variant="body2"
                >
                    You can set a link for this tag from settings.
                </Typography>
                {hasDefaultLink ? (
                    <Typography color="info" variant="body2">
                        Tag contains default link,
                    </Typography>
                ) : null}
            </Stack>
            <Stack spacing={2}>
                <Button
                    color="primary"
                    onClick={onOpenLink}
                    size="large"
                    startIcon={<LaunchIcon fontSize="inherit" />}
                    variant="contained"
                >
                    Open Link
                </Button>
                <Button
                    color="info"
                    onClick={navigateToSettings}
                    size="large"
                    startIcon={<SettingsIcon fontSize="inherit" />}
                    variant="contained"
                >
                    Settings
                </Button>
                <Button
                    color="inherit"
                    onClick={onScanAgain}
                    size="large"
                    startIcon={<ReplayIcon fontSize="inherit" />}
                    variant="outlined"
                >
                    Scan Again
                </Button>
            </Stack>
        </Stack>
    );
}
